import React, { useState, useEffect } from 'react'
import styled from '@emotion/styled'

import { colors } from 'Constants'
import { runOnEnter } from 'Helpers'

import { FadedText, Text } from 'Components/shared/Typography'
import { Flex } from 'lese'

import { ChevronDown } from 'react-feather'

import { Button } from 'Components/shared/Button'

const OptionsContainer = styled(Flex)`
  height: ${({ height }) => (height ? `${height}` : 'auto')};
  display: none;
  position: absolute;
  top: 100%;
  background: ${colors.background};
  width: 100%;
  border: 1px solid ${colors.borderColor};
  border-top: none;
  z-index: 10;
  overflow-y: scroll;
`

const Option = styled(Text)`
  cursor: pointer;
  padding: 12px;
  font-size: 14px;

  :hover {
    background-color: ${colors.background800};
  }
`

const SelectContainer = styled(Flex)`
  position: relative;
  cursor: pointer;
  outline: 0;
  ${({ open }) =>
    open &&
    `${OptionsContainer} {
      display: flex;
    }

    .select-icon {
      transform: rotate(180deg);
    }`}
`

const Select = ({ options, defaultIndex = -1, header, width, xAlign, onChange, height, style }) => {
  const [selection, setSelection] = useState(defaultIndex)
  const [open, setOpen] = useState(false)

  useEffect(() => setSelection(defaultIndex), [defaultIndex])

  const generateChangeHandler = i => () => {
    let shouldContinue = true
    if (typeof onChange === 'function')
      shouldContinue = !(onChange({ previousIndex: selection, newIndex: i }) === false)
    if (shouldContinue) setSelection(i)
    return true
  }

  options =
    options.length > 0
      ? options.map(option => (typeof option === 'string' ? { text: option } : option))
      : [{ text: '' }]

  return (
    <SelectContainer
      tabIndex="0"
      style={{ width }}
      xAlign={xAlign}
      onBlur={() => setOpen(false)}
      onKeyDown={runOnEnter(() => setOpen(!open))}
      onClick={() => setOpen(!open)}
      open={open}
    >
      <Flex style={style} separation="4px" yAlign>
        {selection === -1 && <FadedText>{header}</FadedText>}
        {selection !== -1 && <Text>{options[selection].text}</Text>}
      </Flex>
      <OptionsContainer height={height} column>
        {header && <FadedText style={{ padding: '8px' }}>{header}</FadedText>}
        {options.map((option, i) => (
          <Option onClick={generateChangeHandler(i)}>{option.text}</Option>
        ))}
      </OptionsContainer>

      <ChevronDown className="select-icon" style={{ float: 'right' }} />
    </SelectContainer>
  )
}

const getObjectByValue = (options, id) => {
  if (options) {
    for (const el of options) {
      if (el.value === parseInt(id, 10)) if (options.indexOf(el) !== -1) return options.indexOf(el)
    }
  }
}

const SelectIcon = ({
  options,
  defaultIndex = 0,
  header,
  width,
  xAlign,
  onChange,
  height,
  style
}) => {
  const [selection, setSelection] = useState(getObjectByValue(options, defaultIndex))
  const [open, setOpen] = useState(false)

  useEffect(() => setSelection(getObjectByValue(options, defaultIndex)), [defaultIndex])

  const generateChangeHandler = i => () => {
    let shouldContinue = true
    if (typeof onChange === 'function')
      shouldContinue = !(onChange({ previousIndex: selection, newIndex: i }) === false)
    if (shouldContinue) setSelection(i)
    return true
  }

  options =
    options.length > 0
      ? options.map(option => (typeof option === 'string' ? { text: option } : option))
      : [{ text: '' }]

  return (
    <SelectContainer
      tabIndex="0"
      style={{ width }}
      xAlign={xAlign}
      onBlur={() => setOpen(false)}
      onKeyDown={runOnEnter(() => setOpen(!open))}
      onClick={() => setOpen(!open)}
      open={open}
    >
      <Flex style={style} separation="4px" yAlign>
        <Text>{options[selection]?.text}</Text>
        <ChevronDown className="select-icon" />
      </Flex>
      <OptionsContainer height={height} column>
        {header && <FadedText style={{ padding: '8px' }}>{header}</FadedText>}
        {options.map((option, i) => (
          <Option onClick={generateChangeHandler(i)}>{option.text}</Option>
        ))}
      </OptionsContainer>
    </SelectContainer>
  )
}

const SelectUsingButton = ({ selection, onChange, options, header }) => {
  const [open, setOpen] = useState(false)

  const generateOnChangeHandler = i => () => onChange({ previousIndex: selection, newIndex: i })

  options = options.map(option => (typeof option === 'string' ? { text: option } : option))

  return (
    <SelectContainer
      tabIndex="0"
      onBlur={() => setOpen(false)}
      onKeyDown={runOnEnter(() => setOpen(!open))}
      onClick={() => setOpen(!open)}
      open={open}
    >
      <Button separation="4px" yAlign color={options[selection].color}>
        <Text>{options[selection].text}</Text>
        <ChevronDown className="select-icon" size="16px" />
      </Button>
      <OptionsContainer column>
        {header && <FadedText style={{ padding: '8px' }}>{header}</FadedText>}
        {options.map((option, i) => (
          <Option onClick={generateOnChangeHandler(i)}>{option.text}</Option>
        ))}
      </OptionsContainer>
    </SelectContainer>
  )
}

export { Select, SelectUsingButton, SelectIcon }
